/**
 * VehicleInventoryOverview — Bestandsübersicht mit „Zuletzt gezählt"-Status.
 * Sortiert: nie / am längsten nicht gezählt zuerst. Klick lädt das Fahrzeug
 * in den Scan-Bereich.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Car, ImageOff, Clock, Check, X } from "lucide-react";
import api from "../../lib/api";

const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };
const daysAgo = (iso) => { if (!iso) return null; try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return null; } };

export default function VehicleInventoryOverview({ refreshKey, onPick }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const uncountedOnly = searchParams.get("filter") === "uncounted";

    useEffect(() => {
        let active = true;
        setLoading(true);
        api.get("/dealer/vehicle-inventory/overview")
            .then((r) => { if (active) setData(r.data); })
            .catch(() => { if (active) setData(null); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [refreshKey]);

    if (loading) return <div className="flex items-center gap-2 text-sm text-gray-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Bestand lädt…</div>;
    if (!data) return <div className="text-sm text-gray-400 py-6">Bestand konnte nicht geladen werden.</div>;

    const clearFilter = () => { const n = new URLSearchParams(searchParams); n.delete("filter"); setSearchParams(n, { replace: true }); };
    const allItems = data.items || [];
    const items = uncountedOnly ? allItems.filter((v) => !v.last_counted_at) : allItems;

    const stats = [
        { label: "Fahrzeuge", value: data.total, testid: "vehicle-inventory-stat-total" },
        { label: "Gezählt", value: data.counted, cls: "text-emerald-700", testid: "vehicle-inventory-stat-counted" },
        { label: "Offen", value: data.uncounted, cls: "text-amber-700", testid: "vehicle-inventory-stat-uncounted" },
        { label: "Ohne Bilder", value: data.no_images, cls: "text-rose-700", testid: "vehicle-inventory-stat-no-images" },
    ];

    return (
        <div className="space-y-3" data-testid="vehicle-inventory-overview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {stats.map((s) => (
                    <div key={s.label} className="bg-white border border-gray-200 rounded-sm px-3 py-2" data-testid={s.testid}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{s.label}</div>
                        <div className={`text-xl font-black font-mono-num ${s.cls || "text-[#111827]"}`}>{s.value}</div>
                    </div>
                ))}
            </div>

            {uncountedOnly && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="vehicle-inventory-filter-active">
                    <span className="text-xs font-bold text-amber-800">Filter: Nur „nie gezählte" Fahrzeuge ({items.length})</span>
                    <button type="button" onClick={clearFilter} className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 hover:text-amber-900" data-testid="vehicle-inventory-filter-clear">
                        <X className="w-3.5 h-3.5" /> Filter entfernen
                    </button>
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-[420px] overflow-y-auto" data-testid="vehicle-inventory-list">
                {items.length === 0 && <div className="text-sm text-gray-400 text-center py-6">{uncountedOnly ? "Alle Fahrzeuge wurden bereits gezählt. 🎉" : "Keine Fahrzeuge im Bestand."}</div>}
                {items.map((v) => {
                    const d = daysAgo(v.last_counted_at);
                    return (
                        <button key={v.id} type="button" onClick={() => onPick?.(v)} className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50" data-testid={`vehicle-inventory-list-row-${v.id}`}>
                            <Car className="w-4 h-4 text-gray-300 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold truncate">{v.brand} {v.model} {v.year ? `· ${v.year}` : ""}</div>
                                <div className="text-[11px] text-gray-500 font-mono-num truncate">{v.internal_number}{v.expected_location ? ` · ${v.expected_location}` : ""}</div>
                            </div>
                            {v.image_count === 0 && <ImageOff className="w-3.5 h-3.5 text-rose-500 shrink-0" title="Keine Bilder" />}
                            {v.last_counted_at ? (
                                <span className={`text-[10px] font-bold shrink-0 inline-flex items-center gap-1 ${d > 90 ? "text-amber-600" : "text-emerald-600"}`}>
                                    {d > 90 ? <Clock className="w-3 h-3" /> : <Check className="w-3 h-3" />} {fmtDate(v.last_counted_at)}
                                </span>
                            ) : (
                                <span className="text-[10px] font-bold text-amber-600 shrink-0">nie gezählt</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
